import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, UserCog, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  
  const [form, setForm] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'hr'
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/users`);
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [t]);

  const refetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, form);
      toast.success(t('savedSuccessfully'));
      setDialogOpen(false);
      setForm({ username: '', password: '', email: '', full_name: '', role: 'hr' });
      refetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = t('errorOccurred');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/toggle-active`);
      toast.success(t('savedSuccessfully'));
      refetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'hr': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <UserCog className="w-8 h-8 text-[#E31E24]" />
          <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('users')}</h1>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A] btn-primary-glow" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('newUser')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-[#1A1A1A]">{t('newUser')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-500">{t('username')}</Label>
                <Input
                  value={form.username}
                  onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))}
                  className="bg-white border-gray-200 text-[#1A1A1A]"
                  required
                  data-testid="input-username"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">{t('password')}</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                  className="bg-white border-gray-200 text-[#1A1A1A]"
                  required
                  data-testid="input-password"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">{t('email')}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-white border-gray-200 text-[#1A1A1A]"
                  required
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">{t('fullName')}</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm(prev => ({ ...prev, full_name: e.target.value }))}
                  className="bg-white border-gray-200 text-[#1A1A1A]"
                  required
                  data-testid="input-fullname"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">{t('role')}</Label>
                <Select value={form.role} onValueChange={(v) => setForm(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger className="bg-white border-gray-200 text-[#1A1A1A]" data-testid="select-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="admin">{t('admin')}</SelectItem>
                    <SelectItem value="hr">{t('hr')}</SelectItem>
                    <SelectItem value="viewer">{t('viewer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A]" data-testid="submit-user-btn">
                {t('save')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="table-header">{t('username')}</TableHead>
                  <TableHead className="table-header">{t('fullName')}</TableHead>
                  <TableHead className="table-header">{t('email')}</TableHead>
                  <TableHead className="table-header">{t('role')}</TableHead>
                  <TableHead className="table-header">{t('status')}</TableHead>
                  <TableHead className="table-header text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="table-row" data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-mono text-[#1A1A1A]">{user.username}</TableCell>
                    <TableCell className="text-[#1A1A1A]">{user.full_name}</TableCell>
                    <TableCell className="text-gray-500">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={`border ${getRoleBadgeClass(user.role)}`}>
                        {t(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge className="badge-success">
                          <UserCheck className="w-3 h-3 mr-1" />
                          {t('active')}
                        </Badge>
                      ) : (
                        <Badge className="badge-error">
                          <UserX className="w-3 h-3 mr-1" />
                          {t('inactive')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(user.id)}
                          className="text-gray-500 hover:text-[#1A1A1A]"
                          data-testid={`toggle-user-${user.id}`}
                        >
                          {t('toggleActive')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
